import React, { useMemo, useState } from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { ChartBarIcon, FireIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../lib/format';
import ProvinceMapModalContainer from './ProvinceMapModalContainer';

// GraphQL query to fetch total assets under management for sellers
const GET_SELLER_AND_MAP = gql`
  query GetSellerAndMap {
    sellerValue
    sumByProvinceOptimized
    countryForCompany
  }
`;

const SellerValueMetricContainer = () => {
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const openMapModal = () => setIsMapModalOpen(true);
  const closeMapModal = () => setIsMapModalOpen(false);

  const { data, loading, error } = useQuery(GET_SELLER_AND_MAP, { fetchPolicy: 'network-only' });

  const topFive = useMemo(() => {
    const entries = Object.entries(data?.sumByProvinceOptimized || {});
    const sorted = entries.sort((a, b) => (b[1]?.assets_under_management || 0) - (a[1]?.assets_under_management || 0));
    return sorted.slice(0, 5);
  }, [data]);

  const areaLabel = useMemo(() => {
    const c = (data?.countryForCompany || '').toString().toLowerCase();
    if (c.includes('canada')) return 'Provinces';
    if (c.includes('us') || c.includes('united states') || c.includes('usa')) return 'States';
    return 'States';
  }, [data]);

  const totals = useMemo(() => {
    const vals = Object.values(data?.sumByProvinceOptimized || {});
    return vals.reduce((acc, v) => {
      acc.aum += v?.assets_under_management || 0;
      acc.gp += v?.gross_production || 0;
      return acc;
    }, { aum: 0, gp: 0 });
  }, [data]);

  if (loading) {
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
        <div className="px-4 py-5 sm:p-6 h-full flex flex-col">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-md p-3 bg-succession-primary-10 transition-colors duration-300">
              <ChartBarIcon className="h-6 w-6 text-succession-primary transition-colors duration-300" aria-hidden="true" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-succession-text-secondary transition-colors duration-300">Seller Value</p>
              <p className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching seller value/map:', error);
    return (
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-md p-3 bg-succession-primary-10 transition-colors duration-300">
              <ChartBarIcon className="h-6 w-6 text-succession-primary transition-colors duration-300" aria-hidden="true" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-succession-text-secondary transition-colors duration-300">Seller Value</p>
              <p className="text-lg font-semibold text-succession-error transition-colors duration-300">Error loading data</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Removed formattedValue display in favor of AUM/T12 breakdown

  return (
    <>
      <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 rounded-md p-3 bg-succession-primary-10 transition-colors duration-300">
              <ChartBarIcon className="h-6 w-6 text-succession-primary transition-colors duration-300" aria-hidden="true" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-succession-text-secondary transition-colors duration-300">Seller Market Value</p>
              <div className="text-lg font-semibold text-succession-text-primary transition-colors duration-300 flex items-baseline justify-between gap-3">
                <span>AUM: {formatCurrency(totals.aum)}</span>
                <span className="ml-6">T12: {formatCurrency(totals.gp)}</span>
              </div>
            </div>
          </div>

          {/* Top 5 provinces/states bar chart */}
          <div className="mt-4">
            <p className="text-xs font-medium text-succession-text-secondary mb-2">{`Top 5 Seller ${areaLabel} by AUM`}</p>
            <div className="space-y-2">
              {topFive.map(([name, vals]) => {
                const aum = vals?.assets_under_management || 0;
                const maxAum = topFive.length ? Math.max(...topFive.map(([, v]) => v?.assets_under_management || 0)) : 1;
                const pct = Math.max(6, Math.round((aum / (maxAum || 1)) * 100));
                return (
                  <div key={name} className="w-full">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-succession-text-primary truncate pr-2" title={name}>{name}</span>
                      <span className="text-succession-text-secondary">{formatCurrency(aum)}</span>
                    </div>
                    <div className="h-2 bg-succession-bg-secondary rounded">
                      <div className="h-2 bg-succession-primary rounded" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-4 flex justify-center mt-auto">
            <button
              onClick={openMapModal}
              className="flex items-center space-x-1 hover:opacity-80 transition-opacity duration-300 text-succession-primary text-sm"
              title="View Seller Heat Map"
            >
              <FireIcon className="h-4 w-4 text-succession-warning" aria-hidden="true" />
              <span>View Seller Heat Map</span>
            </button>
          </div>
        </div>
      </div>
      <ProvinceMapModalContainer isOpen={isMapModalOpen} onClose={closeMapModal} />
    </>
  );
};

export default SellerValueMetricContainer; 